import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import useIsMobile from '../../hooks/useIsMobile';

const fmtMXN  = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtAbbr = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000)    return `$${(v / 1000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
};

const appointmentStatusColors = {
  programada: '#3b82f6', confirmada: '#10b981',
  realizada: '#6b7280', cancelada: '#ef4444', reprogramada: '#f59e0b',
};
const appointmentStatusLabels = {
  programada: 'Programada', confirmada: 'Confirmada',
  realizada: 'Realizada', cancelada: 'Cancelada', reprogramada: 'Reprogramada',
};

const roleConfig = {
  notary:     { label: 'Notario',    bg: '#EEF0FB', color: '#4F63D2' },
  supervisor: { label: 'Supervisor', bg: '#DBEAFE', color: '#1D4ED8' },
  staff:      { label: 'Staff',      bg: '#F3F4F6', color: '#374151' },
};

const PALETTE = ['#4F63D2', '#10B981', '#F59E0B', '#EF4444', '#7C3AED', '#06B6D4'];

// ── Timestamp helper ─────────────────────────────────────────────────────────
function timeAgo(date) {
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'ahora mismo';
  if (mins === 1) return 'hace 1 min';
  if (mins < 60) return `hace ${mins} min`;
  return `hace ${Math.floor(mins / 60)}h`;
}

// ── Mini gráfica de barras con etiquetas visibles ────────────────────────────
const MiniBarChart = ({ data, valueKey, labelKey, color = '#4F63D2', isMoney = false }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  if (!data?.length) return <p style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', margin: 0 }}>Sin datos</p>;
  const max = Math.max(...data.map(d => d[valueKey])) || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 64 }}>
      {data.map((d, i) => {
        const isLast    = i === data.length - 1;
        const isHovered = hoveredIdx === i;
        const barH      = Math.max(3, (d[valueKey] / max) * 48);
        const showLabel = isLast || isHovered;
        return (
          <div key={i}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default', position: 'relative' }}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}>
            {/* Tooltip al hover */}
            {isHovered && (
              <div style={{
                position: 'absolute', bottom: barH + 6, left: '50%', transform: 'translateX(-50%)',
                background: '#1e2a4a', color: '#fff', borderRadius: 6, padding: '3px 7px',
                fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
                {d[labelKey]}: {isMoney ? fmtAbbr(d[valueKey]) : d[valueKey]}
              </div>
            )}
            {/* Etiqueta del valor — siempre visible en la última barra */}
            {showLabel && d[valueKey] > 0 && (
              <div style={{ fontSize: 9, color: isLast ? color : '#6B7280', fontWeight: 700, marginBottom: 2 }}>
                {isMoney ? fmtAbbr(d[valueKey]) : d[valueKey]}
              </div>
            )}
            {!showLabel && <div style={{ height: 14 }} />}
            <div
              style={{ width: '100%', height: `${barH}px`, backgroundColor: color, borderRadius: '3px 3px 0 0', opacity: isLast ? 1 : isHovered ? 0.9 : 0.45, transition: 'height 0.3s, opacity 0.15s' }}
            />
          </div>
        );
      })}
    </div>
  );
};

// ── KPI Card — % cambio más prominente ──────────────────────────────────────
const KpiCard = ({ label, value, subLabel, subValue, color, chartData, chartValueKey, chartLabelKey, chartFooter, isMoney }) => (
  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', borderTop: `3px solid ${color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color, letterSpacing: '-0.5px', lineHeight: 1 }}>{value}</div>
    {/* Badge de variación prominente */}
    {subValue && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
          backgroundColor: subValue.startsWith('▲') ? '#D1FAE5' : subValue.startsWith('▼') ? '#FEE2E2' : '#F3F4F6',
          color: subValue.startsWith('▲') ? '#065F46' : subValue.startsWith('▼') ? '#991B1B' : '#6B7280',
        }}>
          {subValue}
        </span>
        {subLabel && <span style={{ fontSize: 11, color: '#9CA3AF' }}>{subLabel}</span>}
      </div>
    )}
    {!subValue && subLabel && <div style={{ fontSize: 12, color: '#9CA3AF' }}>{subLabel}</div>}
    {chartData && (
      <>
        <MiniBarChart data={chartData} valueKey={chartValueKey} labelKey={chartLabelKey} color={color} isMoney={isMoney} />
        {chartFooter && <div style={{ fontSize: 10, color: '#9CA3AF', textAlign: 'right' }}>{chartFooter}</div>}
      </>
    )}
  </div>
);

// ── Section Card ───────────────────────────────────────────────────────────────
const SectionCard = ({ title, action, children, noPadding }) => (
  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{title}</span>
      {action}
    </div>
    <div style={noPadding ? {} : { padding: '16px 18px' }}>{children}</div>
  </div>
);

// ── Agrupar citas por día ─────────────────────────────────────────────────────
function groupByDay(appts) {
  const groups = {};
  (appts || []).forEach(a => {
    const day = new Date(a.start_datetime).toISOString().split('T')[0];
    if (!groups[day]) groups[day] = [];
    groups[day].push(a);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

// ── Main ───────────────────────────────────────────────────────────────────────
const NotaryDashboard = () => {
  const isMobile  = useIsMobile();
  const navigate  = useNavigate();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notaryName, setNotaryName] = useState('');

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [dashRes, notaryRes] = await Promise.all([
        api.get('/dashboard/notary'),
        api.get('/notary-settings').catch(() => ({ data: null })),
      ]);
      setData(dashRes.data);
      setNotaryName(notaryRes.data?.notary_name || '');
      setLastUpdated(new Date());
    } catch {
      toast.error('Error al cargar dashboard ejecutivo');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(refreshKey > 0); }, [fetchAll, refreshKey]); // eslint-disable-line

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: 14 }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <span style={{ fontSize: 13, color: '#9CA3AF' }}>Cargando dashboard ejecutivo...</span>
    </div>
  );

  if (!data) return null;

  const { revenue, case_files, procedure_stats, workload, week_appointments } = data;
  const maxWorkload    = Math.max(...(workload?.map(u => u.active) ?? [1]), 1);
  const appointsByDay  = groupByDay(week_appointments);

  const revenueChange = revenue.change_pct !== null
    ? `${revenue.change_pct >= 0 ? '▲' : '▼'} ${Math.abs(revenue.change_pct)}%`
    : null;

  return (
    <div>
      <PageHeader
        title="Dashboard Ejecutivo"
        subtitle={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>Vista gerencial{notaryName ? ` · ${notaryName}` : ''}</span>
            {lastUpdated && (
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                · Actualizado {timeAgo(lastUpdated)}
              </span>
            )}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Botón refresh */}
            <button
              onClick={() => setRefreshKey(k => k + 1)}
              disabled={refreshing}
              title="Actualizar datos"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1, transition: 'background 0.15s' }}
              onMouseEnter={e => { if (!refreshing) e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
              <svg style={{ width: 14, height: 14, animation: refreshing ? 'spin 0.7s linear infinite' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? 'Actualizando...' : 'Actualizar'}
            </button>
            <Link to="/dashboard"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Dashboard general
            </Link>
          </div>
        }
      />

      {/* ── Fila 1: KPIs ──────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        <KpiCard
          label="Cobrado este mes"
          value={fmtMXN(revenue.this_month)}
          subValue={revenueChange}
          subLabel={revenueChange ? `vs ${fmtMXN(revenue.last_month)} mes ant.` : 'Sin datos mes anterior'}
          color="#10B981"
          chartData={revenue.weekly}
          chartValueKey="total"
          chartLabelKey="week"
          chartFooter="Últimas 8 semanas"
          isMoney
        />
        {/* KPI "Por cobrar" — visualmente consistente con los demás */}
        <KpiCard
          label="Por cobrar (saldo pendiente)"
          value={fmtMXN(revenue.pending)}
          subLabel={
            revenue.pending > 0
              ? <Link to="/accounts-receivable" style={{ color: '#92400E', fontWeight: 600, textDecoration: 'none', fontSize: 11 }}>Ver cuentas por cobrar →</Link>
              : 'Sin saldo pendiente'
          }
          color="#F59E0B"
          chartData={null}
        />
        <KpiCard
          label="Expedientes activos este mes"
          value={case_files.active_this_month}
          subLabel={`${case_files.completed_this_month} cerrado${case_files.completed_this_month !== 1 ? 's' : ''} este mes`}
          color="#4F63D2"
          chartData={case_files.monthly_trend}
          chartValueKey="opened"
          chartLabelKey="label"
          chartFooter="Aperturados · últimos 6 meses"
        />
        <KpiCard
          label="Trámites cerrados este mes"
          value={case_files.completed_this_month}
          color="#7C3AED"
          chartData={case_files.monthly_trend}
          chartValueKey="completed"
          chartLabelKey="label"
          chartFooter="Cerrados · últimos 6 meses"
        />
      </div>

      {/* ── Fila 2: Trámites por tipo + Citas agrupadas por día ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '5fr 7fr', gap: 16, marginBottom: 16 }}>

        {/* Trámites por tipo */}
        <SectionCard title="Trámites por tipo — este mes">
          {!procedure_stats.length ? (
            <EmptyState icon="📋" title="Sin trámites este mes" />
          ) : (
            <>
              {procedure_stats.map((p, i) => {
                const total = procedure_stats.reduce((a, b) => a + b.count, 0) || 1;
                const pct   = Math.round((p.count / total) * 100);
                const color = PALETTE[i % PALETTE.length];
                return (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 12, color: '#4B5563', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '72%' }}>{p.name}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{p.count} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({pct}%)</span></span>
                    </div>
                    <div style={{ height: 7, backgroundColor: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 99, transition: 'width 0.5s' }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #F3F4F6', marginTop: 4 }}>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>Total trámites abiertos este mes</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#4F63D2' }}>{procedure_stats.reduce((a, b) => a + b.count, 0)}</span>
              </div>
            </>
          )}
        </SectionCard>

        {/* Citas de la semana — agrupadas por día */}
        <SectionCard
          title="Citas de la semana"
          noPadding
          action={
            <Link to="/agenda" style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, backgroundColor: '#EEF0FB' }}>
              Ver agenda
            </Link>
          }
        >
          {!week_appointments?.length ? (
            <EmptyState icon="📅" title="No hay citas esta semana" />
          ) : (
            <div>
              {appointsByDay.map(([dayStr, appts]) => {
                const dayDate = new Date(dayStr + 'T00:00:00');
                const isToday = dayStr === new Date().toISOString().split('T')[0];
                return (
                  <div key={dayStr}>
                    {/* Separador de día */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', backgroundColor: isToday ? '#EEF0FB' : '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: isToday ? '#4F63D2' : '#374151', textTransform: 'capitalize' }}>
                        {dayDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'short' })}
                      </span>
                      {isToday && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 20, backgroundColor: '#4F63D2', color: '#fff' }}>Hoy</span>}
                      <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 'auto' }}>{appts.length} cita{appts.length !== 1 ? 's' : ''}</span>
                    </div>
                    {/* Filas de citas — clickeables */}
                    {appts.map(a => {
                      const dt    = new Date(a.start_datetime);
                      const color = appointmentStatusColors[a.status] ?? '#6b7280';
                      return (
                        <div key={a.id}
                          onClick={() => navigate(`/agenda/${a.id}`)}
                          style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto auto', gap: 8, alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid #F9FAFB', cursor: 'pointer', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                            {dt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{a.title}</div>
                            {(a.client?.name || a.user?.name) && (
                              <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                                {a.client?.name ?? '—'}{a.user?.name ? ` · ${a.user.name}` : ''}
                              </div>
                            )}
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: color + '22', color, whiteSpace: 'nowrap' }}>
                            {appointmentStatusLabels[a.status] ?? a.status}
                          </span>
                          <svg style={{ width: 12, height: 12, color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── Fila 3: Carga de trabajo — filas clickeables ──── */}
      <SectionCard
        title="Carga de trabajo por empleado"
        noPadding
        action={
          <Link to="/case-files" style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, backgroundColor: '#EEF0FB' }}>
            Ver expedientes
          </Link>
        }
      >
        {!workload.length ? (
          <EmptyState icon="👥" title="Sin datos de carga de trabajo" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                {['Empleado', 'Rol', 'Activos', 'Sin actividad', 'Completados', 'Carga relativa'].map((h, i) => (
                  <th key={i} className={[1, 4, 5].includes(i) ? 'mobile-hide' : ''}
                    style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workload.map(u => {
                const pct      = Math.round((u.active / maxWorkload) * 100);
                const hasStale = u.stale > 0;
                const rc       = roleConfig[u.role] || roleConfig.staff;
                return (
                  <tr key={u.id}
                    onClick={() => navigate(`/case-files?assigned_user_id=${u.id}`)}
                    style={{ backgroundColor: '#fff', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#4F63D2', fontSize: 12, flexShrink: 0 }}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.name}</span>
                      </div>
                    </td>
                    <td className="mobile-hide" style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, backgroundColor: rc.bg, color: rc.color }}>{rc.label}</span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#4F63D2' }}>{u.active}</span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      {hasStale
                        ? <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, backgroundColor: '#FEE2E2', color: '#991B1B' }}>⚠️ {u.stale}</span>
                        : <span style={{ fontSize: 13, color: '#D1D5DB' }}>—</span>}
                    </td>
                    <td className="mobile-hide" style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#10B981' }}>{u.completed}</span>
                    </td>
                    <td className="mobile-hide" style={{ padding: '11px 14px', minWidth: 160 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, backgroundColor: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: hasStale ? '#EF4444' : '#4F63D2', borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 11, color: '#9CA3AF', minWidth: 28, textAlign: 'right' }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
};

export default NotaryDashboard;
